import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { X, Mail, Clock, RefreshCw, CheckCircle, AlertCircle, User, Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import api from '../api';
import logo from '../assets/T.png';

const SignUp = () => {
  const [formData, setFormData] = useState({ 
    name: '',
    email: '', 
    password: '',
    confirmPassword: '' 
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    form: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Independent Password Field Visibility States
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // OTP Verification Flow States
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [otpAttempts, setOtpAttempts] = useState(3);
  const [registrationData, setRegistrationData] = useState(null);
  const [otpInput, setOtpInput] = useState(['', '', '', '', '', '']);
  const [activeIndex, setActiveIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef([]);
  
  const navigate = useNavigate();

  // Auto-focus first input box when the OTP interface loads up
  useEffect(() => {
    if (showOTPModal) {
      const timer = setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 150);
      setTimeLeft(60);
      setOtpInput(['', '', '', '', '', '']);
      setActiveIndex(0);
      setOtpError(null);
      return () => clearTimeout(timer);
    }
  }, [showOTPModal]);

  // Operational OTP timer countdown
  useEffect(() => {
    if (!showOTPModal || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showOTPModal, timeLeft]);

  const validateForm = () => {
    let valid = true;
    const newErrors = { name: '', email: '', password: '', confirmPassword: '', form: '' };

    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = 'Full name is required (minimum 2 characters)';
      valid = false;
    }

    if (!formData.email) {
      newErrors.email = 'Email address is required';
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      valid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      valid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      valid = false;
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain an uppercase letter';
      valid = false;
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
      valid = false;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
      valid = false;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // OTP Custom Change Handler
  const handleOtpChange = (index, value) => {
    const numericValue = value.replace(/\D/g, '');
    if (!numericValue) {
      const newOtp = [...otpInput];
      newOtp[index] = '';
      setOtpInput(newOtp);
      return;
    }

    const singleDigit = numericValue.slice(-1);
    const newOtp = [...otpInput];
    newOtp[index] = singleDigit;
    setOtpInput(newOtp);
    setOtpError(null);

    if (index < 5) {
      setActiveIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    }

    if (newOtp.every(val => val !== '')) {
      handleOTPVerify(newOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!otpInput[index] && index > 0) {
        const newOtp = [...otpInput];
        newOtp[index - 1] = '';
        setOtpInput(newOtp);
        setActiveIndex(index - 1);
        inputRefs.current[index - 1]?.focus();
      } else {
        const newOtp = [...otpInput];
        newOtp[index] = '';
        setOtpInput(newOtp);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      setActiveIndex(index - 1);
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      setActiveIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Enter') {
      const fullOtp = otpInput.join('');
      if (fullOtp.length === 6) {
        handleOTPVerify(fullOtp);
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim();
    const numbers = pasteData.replace(/\D/g, '').slice(0, 6);
    
    if (numbers.length === 6) {
      setOtpInput(numbers.split(''));
      setActiveIndex(5);
      handleOTPVerify(numbers);
    } else {
      const newOtp = [...otpInput];
      for (let i = 0; i < Math.min(numbers.length, 6); i++) {
        newOtp[i] = numbers[i];
      }
      setOtpInput(newOtp);
      const nextIndex = Math.min(numbers.length, 5);
      setActiveIndex(nextIndex);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  // Verify and complete structural user registration
  const handleOTPVerify = async (otpValue) => {
    const finalCode = otpValue || otpInput.join('');
    if (finalCode.length !== 6) {
      setOtpError('Please enter all 6 digits.');
      return;
    }

    setOtpLoading(true);
    setOtpError(null);

    try {
      const { data } = await api.post('/api/new-auth/register-auth-only', {
        name: registrationData.name,
        email: registrationData.email,
        password: registrationData.password,
        otp: finalCode
      });
      
      setShowOTPModal(false);
      setOtpLoading(false);

      const userId = data.userId;
      if (!userId) throw new Error('Registration payload omitted identifier key.');

      try {
        const appRes = await api.get(`/api/application/${userId}`);
        if (!appRes.data || !appRes.data.application) {
          navigate('/application-form', { state: { userId }, replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          navigate('/application-form', { state: { userId }, replace: true });
          return;
        }
        throw error;
      }

    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Invalid code. Please try again.';
      setOtpError(errorMessage);
      
      if (error.response?.data?.remainingAttempts !== undefined) {
        setOtpAttempts(error.response.data.remainingAttempts);
      }
      if (error.response?.data?.code === 'OTP_EXPIRED') {
        setOtpError('Your validation code has expired. Please request a new one.');
        setOtpAttempts(0);
      }
      if (error.response?.data?.code === 'EMAIL_ALREADY_REGISTERED') {
        setShowOTPModal(false);
        setErrors(prev => ({
          ...prev,
          form: 'This email is already registered. Please login instead.'
        }));
      }
      setOtpLoading(false);
    }
  };

  // Re-trigger OTP Code Generation
  const handleOTPResend = async () => {
    if (isResending || timeLeft > 0) return;
    setOtpError(null);
    setIsResending(true);
    
    try {
      const { data } = await api.post('/api/new-auth/register-auth-only', {
        name: registrationData.name,
        email: registrationData.email,
        password: registrationData.password
      });
      
      if (data.success) {
        setOtpAttempts(3);
        setTimeLeft(60);
        setOtpInput(['', '', '', '', '', '']);
        setActiveIndex(0);
        setTimeout(() => {
          inputRefs.current[0]?.focus();
        }, 150);
      }
    } catch (error) {
      if (error.response?.data?.code === 'RATE_LIMITED') {
        const cooldown = error.response.data.cooldownRemaining || 60;
        setOtpError(`Please wait ${Math.ceil(cooldown / 60)} minute(s) before requesting again.`);
      } else {
        setOtpError(error.response?.data?.message || 'Failed to resend. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  // Primary Submission to acquire temporary context state and dispatch initial verification code
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors(prev => ({ ...prev, form: '' }));

    try {
      const { data } = await api.post('/api/new-auth/register-auth-only', {
        name: formData.name.trim(),
        email: formData.email,
        password: formData.password
      });

      if (data.success && data.requiresOTP) {
        setRegistrationData({
          name: formData.name.trim(),
          email: formData.email,
          password: formData.password
        });
        setShowOTPModal(true);
        setOtpAttempts(3);
        setTimeLeft(60);
        setOtpInput(['', '', '', '', '', '']);
        setIsSubmitting(false);
      } else {
        const userId = data.userId;
        if (userId) {
          navigate('/application-form', { state: { userId }, replace: true });
        }
      }
    } catch (error) {
      if (error.response?.data?.code === 'EMAIL_ALREADY_REGISTERED') {
        setErrors(prev => ({ ...prev, form: 'This email is already registered. Please login instead.' }));
      } else if (error.response?.data?.code === 'RATE_LIMITED') {
        const cooldown = error.response.data.cooldownRemaining || 60;
        setErrors(prev => ({ ...prev, form: `Too many requests. Please wait ${Math.ceil(cooldown / 60)} minute(s).` }));
      } else {
        setErrors(prev => ({ ...prev, form: error.response?.data?.message || 'Failed to send verification code.' }));
      }
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex overflow-hidden w-full">
      
      {/* LEFT COLUMN: Premium Structural Branding Context Bar (Hidden on Mobile Viewports) */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-indigo-900 items-center justify-center p-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-indigo-950 to-slate-900 z-0" />
        {/* Dynamic Abstract Layer Details */}
        <div className="absolute top-0 left-0 w-full h-full opacity-15 pointer-events-none z-0">
          <div className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full bg-indigo-500 blur-3xl" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-blue-500 blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-lg text-left">
          <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center mb-8 border border-white/10">
            <img src={logo} alt="Truyon" className="w-9 h-9 object-contain" />
          </div>
          
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-400 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md mb-4 border border-emerald-500/20">
            🇮🇳 Made In India
          </div>
          
          <h2 className="text-4xl font-extrabold text-white tracking-tight leading-tight mb-4">
            The Smart, Powerful Educational ERP.
          </h2>
          
          <p className="text-indigo-200 text-base font-medium leading-relaxed mb-6">
            Engineered exclusively for all kinds of educational institutions in India. Simplify school, college, and academy administration, fee management, examinations, and digital tracking on a unified ecosystem.
          </p>
          
          <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm px-4 py-3 rounded-xl border border-white/5 max-w-max">
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
            <span className="text-sm font-semibold text-white">Trusted compliance with Indian institutional frameworks</span>
          </div>
        </div>
      </div>

      {/* RIGHT COLUMN: Interactive Registration Flow Container Core Panel */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-4 sm:px-8 md:px-16 lg:px-20 xl:px-24 bg-white relative z-10 min-h-screen overflow-y-auto">
        <div className="w-full max-w-md mx-auto py-8">
          
          {/* Header Identity Display for Mobile Devices */}
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-md">
                <img src={logo} alt="Truyon" className="w-5 h-5 object-contain" />
              </div>
              <span className="text-base font-bold text-slate-900 tracking-tight">Truyon ERP</span>
            </div>
            <span className="text-[11px] font-bold bg-slate-100 text-slate-600 px-2.5 py-1 rounded-md uppercase tracking-wider">
              🇮🇳 Made In India
            </span>
          </div>

          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Create Account</h1>
            <p className="mt-1.5 text-sm sm:text-base text-slate-500">Register your educational institution to launch your workspace.</p>
          </div>

          {/* Master Form Exception Container Context Box */}
          {errors.form && (
            <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-xl flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-rose-800 leading-normal">{errors.form}</p>
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Full Name Input Block Element */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Your Institution Full Name <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <User className="h-4 w-4" />
                </div>
                <input
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl bg-slate-50/50 focus:bg-white transition-all duration-150 outline-none text-sm font-medium text-slate-800 ${
                    errors.name ? 'border-rose-300 focus:border-rose-500 focus:ring-4 focus:ring-rose-50' : 'border-slate-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-50'
                  }`}
                  placeholder="Enter your full name"
                />
              </div>
              {errors.name && <p className="mt-1.5 text-xs font-semibold text-rose-600">{errors.name}</p>}
            </div>

            {/* Email Address Input Block Element */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Your InstitutionEmail Address <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Mail className="h-4 w-4" />
                </div>
                <input
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl bg-slate-50/50 focus:bg-white transition-all duration-150 outline-none text-sm font-medium text-slate-800 ${
                    errors.email ? 'border-rose-300 focus:border-rose-500 focus:ring-4 focus:ring-rose-50' : 'border-slate-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-50'
                  }`}
                  placeholder="institution@example.com"
                />
              </div>
              {errors.email && <p className="mt-1.5 text-xs font-semibold text-rose-600">{errors.email}</p>}
            </div>

            {/* Password Input Block Element */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Password <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Lock className="h-4 w-4" />
                </div>
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-11 py-3 border-2 rounded-xl bg-slate-50/50 focus:bg-white transition-all duration-150 outline-none text-sm font-medium text-slate-800 ${
                    errors.password ? 'border-rose-300 focus:border-rose-500 focus:ring-4 focus:ring-rose-50' : 'border-slate-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-50'
                  }`}
                  placeholder="Create password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p className="mt-1.5 text-xs font-semibold text-rose-600">{errors.password}</p>}
              
              {/* Complex Password Parameter Checklist Grid */}
              <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${formData.password.length >= 8 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  <span>8+ characters</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${/[A-Z]/.test(formData.password) ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  <span>1 uppercase</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${/[0-9]/.test(formData.password) ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  <span>1 number</span>
                </div>
              </div>
            </div>

            {/* Confirm Password Input Block Element */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Confirm Password <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <input
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-11 py-3 border-2 rounded-xl bg-slate-50/50 focus:bg-white transition-all duration-150 outline-none text-sm font-medium text-slate-800 ${
                    errors.confirmPassword ? 'border-rose-300 focus:border-rose-500 focus:ring-4 focus:ring-rose-50' : 'border-slate-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-50'
                  }`}
                  placeholder="Repeat password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="mt-1.5 text-xs font-semibold text-rose-600">{errors.confirmPassword}</p>}
            </div>

            {/* Terms of Service Consent Checkbox */}
            <div className="flex items-start gap-2.5 pt-1">
              <input
                id="terms"
                type="checkbox"
                required
                className="mt-0.5 w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
              />
              <label htmlFor="terms" className="text-xs text-slate-500 leading-normal">
                I accept the{' '}
                <Link to="/terms" className="text-indigo-600 font-semibold hover:underline">Terms of Service</Link>
                {' '}and specify agreement with the{' '}
                <Link to="/privacy" className="text-indigo-600 font-semibold hover:underline">Privacy Policy</Link>.
              </label>
            </div>

            {/* CTA Master Form Submission Element */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3.5 px-4 rounded-xl font-bold text-white shadow-lg transition-all duration-150 flex items-center justify-center gap-2 ${
                isSubmitting ? 'bg-slate-300 text-slate-500 shadow-none cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-100 active:scale-[0.99]'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
                  <span>Requesting verification code...</span>
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Clean Segment Footer Divider Layout */}
          <div className="mt-8 pt-6 border-t border-slate-100 text-center">
            <p className="text-sm text-slate-500">
              Already have an account?{' '}
              <Link to="/signin" className="text-indigo-600 font-bold hover:underline ml-1">
                Sign in
              </Link>
            </p>
          </div>

        </div>
      </div>

      {/* MOBILE-FIRST OPTIMIZED INLINE OTP SHEET / MODAL VIEWPORT COMPONENT */}
      {showOTPModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-200">
          
          <div className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden transform transition-transform duration-300 max-h-[92vh] overflow-y-auto">
            
            {/* Visual Touch Draggable Drag bar Anchor for native Mobile Feel */}
            <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto my-3 sm:hidden" />

            <button
              onClick={() => {
                setShowOTPModal(false);
                setOtpError(null);
              }}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl transition-colors z-10"
              aria-label="Close process view"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Operational Visual Identity Block Banner */}
            <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 px-6 pt-7 pb-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/10 rounded-xl backdrop-blur-md mb-3.5 border border-white/5">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white tracking-tight">Confirm your email</h2>
              <p className="mt-1 text-indigo-100 text-xs sm:text-sm">We sent a verification code to:</p>
              <p className="mt-1.5 text-white font-semibold text-xs bg-white/10 px-3 py-1 rounded-full inline-block border border-white/5 max-w-full truncate">
                {registrationData?.email}
              </p>
            </div>

            {/* Interactive Operations Form Module Area */}
            <div className="p-6">
              
              {/* Conditional Error Element Structure block */}
              {otpError && (
                <div className="mb-5 p-3.5 bg-rose-50 border border-rose-100 rounded-xl flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-rose-800 leading-normal">{otpError}</p>
                </div>
              )}

              <p className="text-sm text-slate-500 text-center mb-6">
                Type the 6-digit confirmation code below to complete setup.
              </p>

              {/* Functional Verification Array Input Field Grid Layout */}
              <div className="flex justify-between gap-2 max-w-xs mx-auto mb-5">
                {otpInput.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    className={`
                      w-11 h-14 text-center text-2xl font-bold rounded-xl border-2
                      focus:outline-none transition-all duration-150 select-all
                      ${digit ? 'border-indigo-600 bg-indigo-50/30 text-indigo-900' : 'border-slate-200 bg-white text-slate-800'}
                      ${activeIndex === index ? 'border-indigo-600 ring-4 ring-indigo-50 shadow-sm' : ''}
                      ${otpError ? 'border-rose-300 bg-rose-50/20 focus:border-rose-500' : ''}
                    `}
                    placeholder="•"
                    disabled={otpLoading}
                  />
                ))}
              </div>

              {/* Dynamic Security Verification Attempt Flags */}
              {otpAttempts < 3 && (
                <p className="text-center text-xs font-semibold text-amber-700 bg-amber-50 rounded-lg py-1.5 px-3 max-w-max mx-auto mb-5">
                  ⚠️ {otpAttempts} attempt(s) remaining before key resets
                </p>
              )}

              {/* Core Clock Parameters / Status Counter Row element container */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm border-t border-b border-slate-100 py-4 mb-6">
                <div className="flex items-center gap-2 text-slate-500">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span>Expires in:</span>
                  <span className={`font-mono font-bold ${timeLeft < 20 ? 'text-rose-600' : 'text-slate-800'}`}>
                    {formatTime(timeLeft)}
                  </span>
                </div>
                
                <button
                  type="button"
                  onClick={handleOTPResend}
                  disabled={isResending || timeLeft > 0}
                  className={`
                    flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold text-sm transition-all duration-150
                    ${timeLeft > 0 || isResending ? 'text-slate-400 cursor-not-allowed' : 'text-indigo-600 hover:bg-indigo-50'}
                  `}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isResending ? 'animate-spin' : ''}`} />
                  {isResending ? 'Sending...' : timeLeft > 0 ? 'Resend blocked' : 'Resend Code'}
                </button>
              </div>

              {/* Inline Action Button Component Validation Trigger */}
              <button
                type="button"
                onClick={() => handleOTPVerify()}
                disabled={otpLoading || otpInput.join('').length !== 6}
                className={`
                  w-full py-4 px-4 rounded-xl font-bold text-white
                  transition-all duration-150 flex items-center justify-center gap-2 shadow-lg
                  ${otpLoading || otpInput.join('').length !== 6
                    ? 'bg-slate-300 text-slate-500 shadow-none cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-100 active:scale-[0.99]'
                  }
                `}
              >
                {otpLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Verifying code...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    <span>Confirm & Register</span>
                  </>
                )}
              </button>

              {/* Assistant Footer Reference Help Text */}
              <p className="mt-5 text-center text-xs text-slate-400">
                Missed the notification? Try inspecting spam folders or_ {' '}
                <button
                  type="button"
                  onClick={handleOTPResend}
                  disabled={isResending || timeLeft > 0}
                  className="text-indigo-600 font-semibold hover:underline disabled:text-slate-400 disabled:no-underline"
                >
                  request reset
                </button>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUp;